// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getTask } from '../api/tasks';
import type { Task, TaskSignatory, TaskAddress } from '../types';
import { TaskStatus } from '../types';

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatShortDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const statusConfig: Record<TaskStatus, { label: string; className: string }> = {
  [TaskStatus.PENDING]: { label: 'Pending', className: 'bg-blue-50 text-blue-700 border-blue-100' },
  [TaskStatus.IN_PROGRESS]: { label: 'In Progress', className: 'bg-yellow-50 text-yellow-700 border-yellow-100' },
  [TaskStatus.COMPLETED]: { label: 'Completed', className: 'bg-green-50 text-green-700 border-green-100' },
  [TaskStatus.REJECTED]: { label: 'Rejected', className: 'bg-red-50 text-red-700 border-red-100' },
  [TaskStatus.CANCELLED]: { label: 'Cancelled', className: 'bg-gray-50 text-gray-600 border-gray-200' },
};

function StatusBadge({ status }: { status: TaskStatus }) {
  const config = statusConfig[status] ?? { label: status, className: 'bg-gray-50 text-gray-600 border-gray-200' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.className}`}>
      {config.label}
    </span>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <h2 className="text-sm font-semibold text-gray-900 mb-4">{title}</h2>
      {children}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-sm font-medium text-gray-900 mt-0.5">{value || '-'}</dd>
    </div>
  );
}

function formatAddress(address?: TaskAddress | null): string {
  if (!address) return '-';
  const parts = [address.line1, address.line2, address.townCity, address.stateProvinceCounty, address.postalCode, address.country].filter(Boolean);
  return parts.join(', ') || '-';
}

export function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDocDialog, setShowDocDialog] = useState(false);

  useEffect(() => {
    if (!taskId) return;
    getTask(taskId)
      .then(setTask)
      .catch(() => setError('Failed to load task details.'))
      .finally(() => setLoading(false));
  }, [taskId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/tasks" className="text-sm text-purple-600 hover:underline">Back to tasks</Link>
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error ?? 'Task not found.'}
        </div>
      </div>
    );
  }

  const meta = task.metadata;
  const brokerage = meta?.brokerage;
  const advisor = meta?.advisor;
  const policy = meta?.policy;
  const signatories = meta?.signatories ?? [];

  return (
    <div className="flex flex-col gap-6 max-w-4xl">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2">
        <Link to="/tasks" className="text-sm text-purple-600 hover:underline">Tasks</Link>
        <span className="text-gray-300">/</span>
        <span className="text-sm text-gray-600 font-mono">{task.policyReferenceCode}</span>
      </div>

      {/* Task header */}
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{task.brokerageName}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {task.taskType === 'DATA_REQUEST' ? 'Data Request' : task.taskType ?? 'Task'}
              {task.policyTypeName ? ` \u00b7 ${task.policyTypeName}` : ''}
            </p>
          </div>
          <StatusBadge status={task.status} />
        </div>
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
          <DetailRow label="Policy Reference" value={task.policyReferenceCode} />
          <DetailRow label="Created" value={task.createdAt ? formatDate(task.createdAt) : undefined} />
          {task.slaDeadline && <DetailRow label="SLA Deadline" value={formatDate(task.slaDeadline)} />}
          {meta?.completedAt && <DetailRow label="LOA Completed" value={formatDate(meta.completedAt)} />}
        </dl>
      </div>

      {/* Brokerage section */}
      {brokerage && (
        <SectionCard title="Brokerage">
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
            <DetailRow label="Name" value={brokerage.name} />
            <DetailRow label="Registration No." value={brokerage.registrationNumber} />
            <DetailRow label="Contact Email" value={brokerage.contactEmail} />
            <DetailRow label="Contact Phone" value={brokerage.contactPhone} />
            <div className="col-span-2">
              <DetailRow label="Address" value={formatAddress(brokerage.address)} />
            </div>
          </dl>
        </SectionCard>
      )}

      {/* Advisor section */}
      {advisor && (
        <SectionCard title="Advisor">
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
            <DetailRow label="Name" value={[advisor.firstName, advisor.lastName].filter(Boolean).join(' ')} />
            <DetailRow label="Email" value={advisor.email} />
            <DetailRow label="Phone" value={advisor.phone} />
            <DetailRow label="License Number" value={advisor.licenseNumber} />
          </dl>
        </SectionCard>
      )}

      {/* Policy section */}
      {policy && (
        <SectionCard title="Policy">
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
            <DetailRow label="Policy Number" value={policy.policyNumber} />
            <DetailRow label="Reference Code" value={policy.referenceCode} />
            <DetailRow label="Policy Type" value={policy.policyTypeName} />
            <DetailRow label="Category" value={policy.policyTypeCategory} />
            {meta?.profile?.name && <DetailRow label="Profile" value={meta.profile.name} />}
            {meta?.isFirstLoaForPolicy !== undefined && (
              <DetailRow label="First LOA" value={meta.isFirstLoaForPolicy ? 'Yes' : 'No (Renewal)'} />
            )}
          </dl>
        </SectionCard>
      )}

      {/* Signatories section */}
      {signatories.length > 0 && (
        <SectionCard title={`Signatories (${signatories.length})`}>
          <div className="space-y-4">
            {signatories.map((sig: TaskSignatory, idx: number) => (
              <div
                key={idx}
                className={`${idx > 0 ? 'pt-4 border-t border-gray-100' : ''}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-gray-900">
                    {[sig.firstName, sig.lastName].filter(Boolean).join(' ') || 'Unknown'}
                  </span>
                  {sig.relationshipType && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                      {sig.relationshipType.replace(/_/g, ' ')}
                    </span>
                  )}
                  {sig.hasOverrides && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                      Overrides Applied
                    </span>
                  )}
                </div>
                <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                  <DetailRow label="Email" value={sig.email} />
                  <DetailRow label="Phone" value={sig.phone} />
                  {sig.dateOfBirth && <DetailRow label="Date of Birth" value={formatShortDate(sig.dateOfBirth)} />}
                  {sig.signedAt && <DetailRow label="Signed At" value={formatDate(sig.signedAt)} />}
                  {sig.address && (
                    <div className="col-span-2">
                      <DetailRow label="Address" value={formatAddress(sig.address)} />
                    </div>
                  )}
                  {sig.overrideReason && (
                    <div className="col-span-2">
                      <DetailRow label="Override Reason" value={sig.overrideReason} />
                    </div>
                  )}
                </dl>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* View LOA Document button */}
      {task.hasDocument && (
        <div className="flex justify-start">
          <button
            type="button"
            onClick={() => setShowDocDialog(true)}
            className="px-4 py-2 rounded-lg border border-purple-200 bg-purple-50 text-purple-700 text-sm font-medium hover:bg-purple-100 transition-colors"
          >
            View LOA Document
          </button>
        </div>
      )}

      {/* LOA Document Dialog */}
      {showDocDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDocDialog(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-[90vw] h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">
                LOA Document &mdash; {task.policyReferenceCode}
              </h3>
              <button
                type="button"
                onClick={() => setShowDocDialog(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                &times;
              </button>
            </div>
            <div className="flex-1 p-2">
              <iframe
                src={`/api/tasks/${task.id}/documents/LOA_SLICE`}
                className="w-full h-full rounded-lg border border-gray-200"
                title="LOA Document"
              />
            </div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {(task.status === TaskStatus.PENDING || task.status === TaskStatus.IN_PROGRESS) && (
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(`/tasks/${task.id}/form`)}
            className="px-5 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Enter Details Manually
          </button>
          <button
            type="button"
            className="px-5 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition-colors"
          >
            Automatically Fill
          </button>
        </div>
      )}

    </div>
  );
}
